// Biosecurity classification for hazards in the Biosecurity category.
//
// Four INDEPENDENT axes — a `follow_up` report can carry a `confirmed_detection`
// that is now `contained` in the `animal` domain — so they are four separate
// fields, not one status enum. Domain values live here rather than being inferred
// at display time so a hazard's classification stays stable if the source form is
// later reworded.
//
// Mirrors the BIOSECURITY_* constants on the PHP Hazard model (which range-checks
// every value on save) — keep the two in sync.

const option = (value, label) => ({ value, label });

/** What kind of report this is. */
export const BIOSECURITY_REPORT_TYPES = [
  option('sighting', 'Sighting'),
  option('initial_notification', 'Initial notification'),
  option('diagnostic', 'Diagnostic'),
  option('investigation', 'Investigation'),
  option('surveillance', 'Surveillance'),
  option('follow_up', 'Follow-up'),
  option('final', 'Final'),
  option('breach', 'Breach'),
];

/** What kind of event is being reported. */
export const BIOSECURITY_EVENT_TYPES = [
  option('suspected_detection', 'Suspected detection'),
  option('confirmed_detection', 'Confirmed detection'),
  option('outbreak', 'Outbreak'),
  option('exposure', 'Exposure'),
  option('pathway_incident', 'Pathway incident'),
  option('non_compliance', 'Non-compliance'),
  option('response_action', 'Response action'),
];

/** Where the event has got to. Distinct from the hazard's own workflow status. */
export const BIOSECURITY_EVENT_STATUSES = [
  option('open', 'Open'),
  option('under_investigation', 'Under investigation'),
  option('ruled_out', 'Ruled out'),
  option('confirmed', 'Confirmed'),
  option('contained', 'Contained'),
  option('eradicated', 'Eradicated'),
  option('stable', 'Stable'),
  option('closed', 'Closed'),
];

/** Which biological domain the event sits in. */
export const BIOSECURITY_DOMAINS = [
  option('plant', 'Plant'),
  option('animal', 'Animal'),
  option('aquatic', 'Aquatic'),
  option('bee', 'Bee'),
  option('marine', 'Marine'),
  option('weed', 'Weed'),
  option('environmental', 'Environmental'),
  option('commodity', 'Commodity'),
];

/** The four axes, in the order they should be presented. */
export const BIOSECURITY_TAXONOMY = [
  {
    name: 'report_type',
    label: 'Report type',
    options: BIOSECURITY_REPORT_TYPES,
    tooltip: 'What kind of report this is — a first sighting, a diagnostic result, a follow-up, or a final report.',
  },
  {
    name: 'event_type',
    label: 'Event type',
    options: BIOSECURITY_EVENT_TYPES,
    tooltip: 'What kind of event is being reported — a suspected or confirmed detection, an outbreak, an exposure, a pathway incident, non-compliance, or a response action.',
  },
  {
    name: 'event_status',
    label: 'Event status',
    options: BIOSECURITY_EVENT_STATUSES,
    tooltip: 'Where the biosecurity event has got to. Separate from the hazard’s own workflow status.',
  },
  {
    name: 'domain',
    label: 'Domain',
    options: BIOSECURITY_DOMAINS,
    tooltip: 'Which biological domain the event sits in.',
  },
];

const labelFrom = (options, value) =>
  (options.find((o) => o.value === value) || {}).label || value || null;

export const getBiosecurityReportTypeLabel = (v) => labelFrom(BIOSECURITY_REPORT_TYPES, v);
export const getBiosecurityEventTypeLabel = (v) => labelFrom(BIOSECURITY_EVENT_TYPES, v);
export const getBiosecurityEventStatusLabel = (v) => labelFrom(BIOSECURITY_EVENT_STATUSES, v);
export const getBiosecurityDomainLabel = (v) => labelFrom(BIOSECURITY_DOMAINS, v);
